// Enhanced Bland Call Workflow Types
// These match the Pydantic models in backend/app/defs/enhanced_bland_call.py

export enum EnhancedBlandWorkflowStatus {
  SCHEDULED = 'scheduled',
  STARTED = 'started',
  THINKING = 'thinking',
  CALL_IN_PROGRESS = 'call_in_progress',
  WAITING = 'waiting',
  COMPLETED = 'completed',
  FAILED_INELIGIBLE = 'ineligible',
  FAILED_READY_CHECK = 'waiting_info',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

// Normalized success status - string-based (replaces integer enum)
export type NormalizedSuccessStatus =
  | 'success'
  | 'full_success'
  | 'partial_success'
  | 'no_success'
  | 'failure'
  | 'uncontrollable'
  | 'unclear';

// Display mapping for success status
export const SUCCESS_STATUS_DISPLAY: Record<
  NormalizedSuccessStatus,
  { label: string; className: string }
> = {
  success: { label: 'Success', className: 'text-green-700' },
  full_success: { label: 'Full Success', className: 'text-green-700' },
  partial_success: { label: 'Partial Success', className: 'text-amber-700' },
  no_success: { label: 'No Success', className: 'text-red-700' },
  failure: { label: 'Failed', className: 'text-red-700' },
  uncontrollable: { label: 'Uncontrollable', className: 'text-gray-600' },
  unclear: { label: 'Unclear', className: 'text-gray-600' },
};

// Normalized evaluation schema - matches backend NormalizedEvaluationSchema
export interface NormalizedEvaluationSchema {
  // Always present
  success_status: NormalizedSuccessStatus;
  call_summary: string;

  // Claims evaluation fields (optional)
  confidence?: number | null;
  score?: number | null;
  info?: Array<Record<string, string | null>> | null;
  issues?: string[] | null;
  violated_guidelines?: string[] | null;
  follow_up?: string | null;
  quotes?: string[] | null;

  // Medical Records evaluation fields (optional)
  alternative_submission_methods?: string[] | null;
  alternates?: Array<Record<string, any>> | null;
  action_items?: string[] | null;
  resend_same_contact?: boolean | null;
  resend_same_contact_reason?: string | null;
  request_status?: string | null;
  notification_category?: string | null;
  notification_subcategory?: string | null;
  key_information?: Record<string, string | null> | null;
  reached_human?: boolean | null;
}

export interface CallResultSchema {
  id: string;
  workflow_id: string;
  call_id?: string;
  phone_number?: string;
  status?: string;
  created_at?: string | null;
  started_at?: string | null;
  duration?: number | null;
  duration_seconds?: number;
  summary?: string | null;
  transcript_available: boolean;
  evaluation_available: boolean;
  evaluation?: NormalizedEvaluationSchema | null;
  error?: string | null;
  error_message?: string;
  transcript?: string;
}

export interface EventLogEntry {
  title: string;
  description: string;
  timestamp: string; // ISO timestamp string
}

// BPO Actions
export type BPOActionType =
  | 'requested'
  | 'call_started'
  | 'completed'
  | 'expired'
  | 'cancelled'
  | 'failed';

export interface BPOAction {
  type: BPOActionType;
  requested_at?: string | null;
  received_at?: string | null;
  expired_at?: string | null;
  cancelled_at?: string | null;
  reason?: string | null;
  channel_id?: string | null;
  message_ts?: string | null;
  sla_minutes?: number | null;
  sla_due_at?: string | null;
  dialpad_call_id?: string | null;
  ringcentral_call_id?: string | null;
  metadata?: Record<string, any> | null;
}

export interface SimpleReadinessInfo {
  missing_items: string[];
  summary: string;
  is_ready: boolean;
}

export interface TaskSummary {
  group_canonical: string;
  group_name: string;
  matter_name: string;
  entity_name?: string | null;
  created_at: string;
  due_date?: string | null;
}

export interface EnhancedBlandWorkflowState {
  workflow_id: string;
  task_id: string;
  status: EnhancedBlandWorkflowStatus;
  last_message?: string;
  /** Human-friendly status message coming from the backend */
  display_message?: string;
  readiness: SimpleReadinessInfo;
  max_calls_allowed: number;
  event_log: EventLogEntry[];
  call_results: CallResultSchema[];
  error_message?: string | null;
  created_at?: string;
  updated_at?: string;
  calls_made: number;
  task_summary?: TaskSummary;
}

// Voice AI Call Evaluation Result - matches AnalyzeVoiceAiCallEvaluationSchema
export interface VoiceAICallEvaluationResult {
  call_summary: string;
  alternative_submission_methods: string[];
  alternates: Array<{
    handle_type: 'fax' | 'mailing_address';
    value: string;
    label: string | null;
    confidence: number | null;
  }>;
  action_items: string[];
  request_status: string;
  success: boolean;
  reached_human: boolean;
}

export interface VoiceAIAgentRunSchema {
  id: string;
  workflow_id: string;
  status: EnhancedBlandWorkflowStatus;
  scheduled_for?: string | null; // ISO string
  calls_made: number;
  max_calls_allowed: number;
  summary?: string | null;
  event_log?: EventLogEntry[];
  messages?: Array<Record<string, any>>;
  error_message?: string;
  created_at?: string;
  updated_at?: string;
  call_results: CallResultSchema[];
  task_summary?: TaskSummary;
  evaluation_result?: VoiceAICallEvaluationResult | null;
  bpo_actions?: BPOAction[] | null;
}

// New hierarchical types for enhanced debugging

export interface EnhancedCallResultSchema extends CallResultSchema {
  duration_minutes?: number | null;
  cost_usd?: number | null;
  answered_by?: string | null;
  disposition_tag?: string | null;
  transcript_length?: number | null;
  created_at?: string | null; // Add created_at field
}

export interface ScheduledCallInsight {
  run_id: string;
  task_id: string;
  task_name: string;
  entity_name?: string | null;
  scheduled_for: string; // ISO string
  scheduled_reasoning?: string | null;
  time_until_execution?: string | null;
  is_overdue: boolean;
}

export interface EnhancedVoiceAIAgentRunSchema
  extends Omit<VoiceAIAgentRunSchema, 'call_results'> {
  task_id: string; // Ensure this is present
  scheduled_reasoning?: string | null;
  call_results: EnhancedCallResultSchema[];
  total_call_cost?: number | null;
  avg_call_duration?: number | null;
  success_rate?: number | null;
}

export interface MatterVoiceAgentSummary {
  matter_id: string;
  matter_name: string;
  total_runs: number;
  total_calls: number;
  total_cost?: number | null;
  scheduled_calls_count: number;
  overdue_calls_count: number;
  active_runs_count: number;
  completed_runs_count: number;
  failed_runs_count: number;
  cancelled_runs_count: number;
  avg_success_rate?: number | null;
  last_activity?: string | null; // ISO string

  // Grouped runs by task type
  runs: EnhancedVoiceAIAgentRunSchema[];
  scheduled_insights: ScheduledCallInsight[];
  // New hierarchical structure: tasks with their runs
  tasks_data?: Record<
    string,
    {
      task: TaskSummary;
      runs: EnhancedVoiceAIAgentRunSchema[];
    }
  >;
}

export interface VoiceAgentCommandCenterResponse {
  matters: MatterVoiceAgentSummary[];
  total_matters: number;
  global_stats: VoiceAgentGlobalStats;
  // Pagination metadata
  page: number;
  page_size: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface VoiceAgentGlobalStats {
  total_matters_with_activity: number;
  total_runs: number;
  total_calls: number;
  total_cost?: number | null;
  scheduled_calls_count: number;
  overdue_calls_count: number;

  // Status breakdown
  runs_by_status: Record<string, number>;
  calls_by_status: Record<string, number>;

  // Time-based insights
  calls_last_24h: number;
  calls_last_7d: number;
  avg_calls_per_matter?: number | null;

  // Cost insights
  cost_last_24h?: number | null;
  cost_last_7d?: number | null;
  avg_cost_per_call?: number | null;
}

// BPO Command Center Types
export interface BPOSummaryRow {
  matter_id: string;
  workflow_id: string;
  matter_name: string;
  coverage_id?: string | null;
  first_time_requested: string | null;
  phone_number_to: string | null;
  first_time_called: string | null;
  from_number: string | null;
  agent_username?: string | null;
  completed_status: string | null;
  completed_call_success: boolean | null;
  completed_call_outcome?: string | null;
  call_issues?: string | null;
  calls_made?: number | null;
  initiated_by_user_id?: string | null;
  initiated_by_user_email?: string | null;
  initiated_by_user_name?: string | null;
}

export interface BPOSummaryResponse {
  count: number;
  rows: BPOSummaryRow[];
}

// Medical Records BPO Types
export interface MedicalRecordsBPOSummaryRow {
  matter_id: string;
  workflow_id: string;
  matter_name: string;
  provider_id?: string | null;
  provider_name?: string | null;
  treatment_request_id?: string | null;
  first_time_requested: string | null;
  phone_number_to: string | null;
  first_time_called: string | null;
  from_number: string | null;
  completed_status: string | null;
  outcome?: string | null;
  outcome_display?: string | null;
  outcome_details?: {
    outcome?: string;
    delivery_method?: string;
    date_sent?: string;
    delivery_details?: string;
    estimated_timeframe?: string;
    followup_contact_name?: string;
    correct_phone_number?: string;
    department_extension?: string;
    resend_method?: string;
    destination_details?: string;
    resubmission_verification_method?: string;
    reason?: string;
    representative_name?: string;
    unable_to_reach_outcome?: string;
    issue_type?: string;
    requires_manual_review?: boolean;
  };
  calls_made?: number | null;
  initiated_by_user_id?: string | null;
  initiated_by_user_email?: string | null;
  initiated_by_user_name?: string | null;
}

export interface MedicalRecordsBPOSummaryResponse {
  count: number;
  rows: MedicalRecordsBPOSummaryRow[];
}

export interface CommandCenterStats {
  matters_with_activity: number;
  total_runs: number;
  total_calls: number;
  active_runs: number;
  scheduled_runs: number;
  cancelled_runs: number;
  overdue_runs: number;
  recent_calls_24h: number;
  total_cost_usd: number;
  timestamp: string;
}

export interface VoiceAIAgentRunsResponse {
  task_id: string;
  runs: VoiceAIAgentRunSchema[];
  count: number;
}

export interface EnhancedBlandWorkflowsResponse {
  workflows: EnhancedBlandWorkflowState[];
  count: number;
}

// Request/Response types
export interface StartEnhancedBlandWorkflowRequest {
  task_id: string;
  preferred_phone_number?: string;
  max_calls_override?: number;
  attachment_blob_ids?: string[];
  /** Use AI Hold & Transfer mode: AI waits on hold, transfers to user when human answers */
  use_hold_and_transfer?: boolean;
}

export interface StartEnhancedBlandWorkflowResponse {
  message: string;
  workflow_id: string;
  task_id: string;
}

export interface RetryEnhancedBlandWorkflowResponse {
  message: string;
  task_id: string;
}

// Utility functions
export const getStatusColor = (status: EnhancedBlandWorkflowStatus): string => {
  switch (status) {
    case EnhancedBlandWorkflowStatus.COMPLETED:
      return 'text-green-700 bg-green-50 ring-green-600/20';
    case EnhancedBlandWorkflowStatus.FAILED_INELIGIBLE:
    case EnhancedBlandWorkflowStatus.FAILED:
      return 'text-red-700 bg-red-50 ring-red-600/20';
    case EnhancedBlandWorkflowStatus.CANCELLED:
      return 'text-orange-700 bg-orange-50 ring-orange-600/20';
    case EnhancedBlandWorkflowStatus.FAILED_READY_CHECK:
      return 'text-yellow-800 bg-yellow-50 ring-yellow-600/20';
    case EnhancedBlandWorkflowStatus.CALL_IN_PROGRESS:
      return 'text-blue-700 bg-blue-50 ring-blue-600/20';
    case EnhancedBlandWorkflowStatus.THINKING:
    case EnhancedBlandWorkflowStatus.STARTED:
    case EnhancedBlandWorkflowStatus.WAITING:
      return 'text-gray-600 bg-gray-50 ring-gray-500/10';
    case EnhancedBlandWorkflowStatus.SCHEDULED:
      return 'text-purple-700 bg-purple-50 ring-purple-600/20';
    default:
      return 'text-gray-600 bg-gray-50 ring-gray-500/10';
  }
};

export const getStatusIcon = (status: EnhancedBlandWorkflowStatus): string => {
  switch (status) {
    case EnhancedBlandWorkflowStatus.COMPLETED:
      return '✓';
    case EnhancedBlandWorkflowStatus.FAILED_INELIGIBLE:
    case EnhancedBlandWorkflowStatus.FAILED:
      return '✗';
    case EnhancedBlandWorkflowStatus.CANCELLED:
      return '⚠️';
    case EnhancedBlandWorkflowStatus.FAILED_READY_CHECK:
    case EnhancedBlandWorkflowStatus.WAITING:
      return '⏳';
    case EnhancedBlandWorkflowStatus.CALL_IN_PROGRESS:
      return '📞';
    case EnhancedBlandWorkflowStatus.THINKING:
    case EnhancedBlandWorkflowStatus.STARTED:
      return '🤔';
    case EnhancedBlandWorkflowStatus.SCHEDULED:
      return '⏰';
    default:
      return '•';
  }
};

// Police Outreach Scheduling Types
export interface PoliceOutreachSummary {
  matter_id: string;
  total_runs: number;
  scheduled_count: number;
  active_count: number;
  completed_count: number;
  failed_count: number;
  upcoming_calls: UpcomingCall[];
  next_call: UpcomingCall | null;
  last_updated: string;
}

export interface UpcomingCall {
  run_id: string;
  task_id: string;
  scheduled_for: string;
  task_name: string | null;
  police_department: string | null;
}

export interface ScheduleOutreachRequest {
  matter_id: string;
  police_report_id: string;
  when: string; // ISO datetime string
  force?: boolean;
}

export interface ScheduleOutreachResponse {
  success: boolean;
  run_id: string;
  task_id: string;
  police_report_id: string;
  scheduled_for: string | null;
  status: string;
  message: string;
}

export interface BulkScheduleRequest {
  matter_id: string;
  schedules: Array<{
    police_report_id: string;
    when: string;
  }>;
}

export interface BulkScheduleResponse {
  matter_id: string;
  total_requested: number;
  successful: number;
  failed: number;
  results: Array<{
    police_report_id: string;
    success: boolean;
    run_id?: string;
    scheduled_for?: string;
    error?: string;
  }>;
}

export interface CancelOutreachRequest {
  run_id: string;
  reason?: string;
}

export interface CancelOutreachResponse {
  success: boolean;
  run_id: string;
  message: string;
  reason?: string;
}

export interface TriggerOutreachResponse {
  success: boolean;
  run_id: string;
  message: string;
}

// Workflow Monitoring Types
export interface WorkflowResult {
  workflow_id: string;
  started_at: string;
  completed_at?: string;
  duration_seconds?: number;
  status: 'running' | 'completed' | 'failed' | 'partial_failure';
  errors: string[];
}

export interface PoliceSchedulerResult extends WorkflowResult {
  matter_id: string;
  scheduled_runs: number;
  reasoning: string;
  run_ids?: string[];
}

export interface MassSchedulerResult extends WorkflowResult {
  total_matters: number;
  successful_matters: number;
  failed_matters: number;
  total_scheduled_runs: number;
  summary: string;
  matter_results?: Record<string, PoliceSchedulerResult>;
}
